import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  normalizePpeStatus,
  PPE_STATUS_I18N_KEYS,
  PpeDetectionStatus,
} from 'app/services/PPE/ppe.models';

interface ChipStyle {
  icon: string | null;
  classes: string;
}

/** Colours per status; both light and dark Fuse schemes. */
const CHIP_STYLES: Record<PpeDetectionStatus, ChipStyle> = {
  NOT_DETECTED: {
    icon: 'remove_circle_outline',
    classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
  DETECTION_IN_PROGRESS: {
    icon: null, // spinner instead
    // blue-900 sinks into the navy dark page, so dark mode uses one step lighter.
    classes: 'bg-blue-50 text-blue-700 dark:bg-blue-800 dark:text-blue-100',
  },
  DETECTED: {
    icon: 'check_circle',
    classes: 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-200',
  },
  PARTIALLY_DETECTED: {
    icon: 'warning',
    classes: 'bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
  },
  DETECTION_FAILED: {
    icon: 'error',
    classes: 'bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-200',
  },
};

/**
 * One PPE detection status as a small chip (V2CORE-2901).
 * `status` accepts any backend value; unknown / null shows "Not detected".
 * `count` (AI observation count) is shown for Detected and Partly detected when above 0.
 */
@Component({
  selector: 'app-ppe-status-chip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      *transloco="let t"
      class="ppe-status-chip inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap"
      [ngClass]="[style.classes, dense ? 'px-1.5 py-0.5 text-xs' : 'px-2.5 py-1 text-sm']"
      [attr.data-status]="normalizedStatus"
      [attr.aria-label]="t('ppe.status.aria', { status: label(t) })"
      [matTooltip]="t('ppe.status.aria', { status: label(t) })"
      role="status"
    >
      <!-- Same 16px box as the icons, so every status chip has the same height. -->
      <span
        *ngIf="!style.icon"
        class="ppe-status-chip__spinner-box flex items-center justify-center w-4 h-4 shrink-0"
        aria-hidden="true"
      >
        <mat-progress-spinner
          class="ppe-status-chip__spinner"
          mode="indeterminate"
          [diameter]="12"
          [strokeWidth]="2"
        ></mat-progress-spinner>
      </span>
      <mat-icon
        *ngIf="style.icon"
        class="ppe-status-chip__icon icon-size-4 text-current"
        aria-hidden="true"
        >{{ style.icon }}</mat-icon
      >
      <span class="ppe-status-chip__label">{{ label(t) }}</span>
    </span>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }
      /* Tajawal leaves a deep gap under its letters, so a centred line box puts the text
         about 3px above the icon. Trimming the box to the letters centres them. */
      .ppe-status-chip__label {
        text-box: trim-both cap alphabetic;
      }
      .ppe-status-chip__spinner ::ng-deep circle {
        stroke: currentColor !important;
      }
    `,
  ],
})
export class PpeStatusChipComponent {
  /** Backend status value. */
  @Input() status: PpeDetectionStatus | string | null | undefined;
  /** AI observation count, shown for Detected / Partly detected when above 0. */
  @Input() count?: number | null;
  /** Smaller chip for table cells and floor rows. */
  @Input() dense = false;

  get normalizedStatus(): PpeDetectionStatus {
    return normalizePpeStatus(this.status);
  }

  get style(): ChipStyle {
    return CHIP_STYLES[this.normalizedStatus];
  }

  get labelKey(): string {
    return PPE_STATUS_I18N_KEYS[this.normalizedStatus];
  }

  /** Whether the count is part of the label. */
  get showsCount(): boolean {
    const status = this.normalizedStatus;
    return (
      (status === 'DETECTED' || status === 'PARTIALLY_DETECTED') &&
      this.count !== null &&
      this.count !== undefined &&
      Number.isFinite(Number(this.count)) &&
      // "(0)" reads as a broken count (e.g. right after remove-all): show the plain label.
      Number(this.count) > 0
    );
  }

  label(t: (key: string, params?: Record<string, unknown>) => string): string {
    return this.showsCount
      ? t(`${this.labelKey}_count`, { count: Number(this.count) })
      : t(this.labelKey);
  }
}
